import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import {
  getTrainingSettings,
  updateTrainingSettings,
} from '../../services/strava';

export function UserOnboarding() {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuthStore();
  
  const [isVisible, setIsVisible] = useState(() => {
    if (localStorage.getItem('onboarding_skipped')) return false;
    return !!localStorage.getItem('pelo_needs_onboarding');
  });
  const [isSaving, setIsSaving] = useState(false);
  const [mobileStep, setMobileStep] = useState(0);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isVisible) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isVisible]);
  
  // Form states
  const [ftp, setFtp] = useState('');
  const [maxHr, setMaxHr] = useState('');
  const [noPowerMeter, setNoPowerMeter] = useState(false);

  useEffect(() => {
    // Only check for authenticated users
    if (!isAuthenticated || !user) return;

    // Check if user already skipped onboarding in this session/browser
    const isSkipped = localStorage.getItem('onboarding_skipped');
    if (isSkipped) return;

    checkSettings();
  }, [isAuthenticated, user?.id]);

  async function checkSettings() {
    try {
      const data = await getTrainingSettings();
      
      // Show modal if FTP or MaxHR is missing
      if (!data.ftp || !data.maxHeartRate) {
        setIsVisible(true);
        localStorage.setItem('pelo_needs_onboarding', 'true');
        // Pre-fill existing values if any
        if (data.ftp) setFtp(data.ftp.toString());
        if (data.maxHeartRate) setMaxHr(data.maxHeartRate.toString());
      } else {
        localStorage.removeItem('pelo_needs_onboarding');
      }
    } catch (error) {
      console.error('Failed to checked training settings:', error);
    }
  }

  async function handleSave() {
    if ((!noPowerMeter && !ftp) || !maxHr) return;

    setIsSaving(true);
    try {
      await updateTrainingSettings({
        ftp: noPowerMeter ? null : parseInt(ftp, 10),
        maxHeartRate: parseInt(maxHr, 10),
      });
      localStorage.removeItem('pelo_needs_onboarding');
      setIsVisible(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert(t('common.error'));
    } finally {
      setIsSaving(false);
    }
  }

  function handleSkip() {
    if (!confirm(t('onboarding.skipConfirm'))) return;
    localStorage.setItem('onboarding_skipped', 'true');
    localStorage.removeItem('pelo_needs_onboarding');
    setIsVisible(false);
  }

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal Content - Wider for 2 columns, scrollable on mobile if needed */}
      <div className="relative w-full max-w-4xl bg-[#141414] border border-white/10 rounded-2xl shadow-2xl max-h-[90dvh] flex flex-col md:block overflow-hidden">
        {/* Header decoration */}
        <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-[#e10600] to-[#ff8700] z-10" />
        
        <div className="grid grid-cols-1 md:grid-cols-2 h-full overflow-y-auto md:overflow-visible">
          {/* Left Column: Visual Preview */}
          <div className={`${mobileStep === 1 ? 'hidden' : 'flex'} md:flex relative p-6 md:p-8 bg-gradient-to-br from-black to-neutral-900 border-b md:border-b-0 md:border-r border-white/5 flex-col justify-between overflow-hidden shrink-0`}>
            {/* Background elements */}
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>
            <div className="absolute -top-20 -left-20 w-64 h-64 bg-[#e10600] rounded-full blur-[100px] opacity-10"></div>
            
            <div className="relative z-10 mb-6 md:mb-0">
              <h2 className="text-xl md:text-2xl font-black text-white italic tracking-tight font-display mb-2">
                {t('onboarding.visual.unlockTitle')}
              </h2>
              <p className="text-white/60 text-sm">
                {t('onboarding.visual.unlockDesc')}
              </p>
            </div>

            {/* Comparison Visual */}
            <div className="relative z-10 my-4 md:my-8 space-y-6">
              {/* Without Setup - ALWAYS VISIBLE */}
              <div className="opacity-50 grayscale transition-all duration-500">
                 <div className="flex items-center justify-between mb-2">
                   <span className="text-xs font-bold text-white/40 uppercase">{t('onboarding.visual.withoutSetup')}</span>
                   <span className="text-xs font-mono text-white/30">{t('onboarding.visual.rawDataOnly')}</span>
                 </div>
                 <div className="flex gap-2">
                   <div className="flex-1 bg-white/5 p-3 rounded-lg border border-white/5">
                     <span className="text-[10px] text-white/40 block">{t('onboarding.visual.speed')}</span>
                     <span className="text-xl font-mono text-white">32<span className="text-xs text-white/40 ml-1">km/h</span></span>
                   </div>
                   <div className="flex-1 bg-white/5 p-3 rounded-lg border border-white/5">
                      <span className="text-[10px] text-white/40 block">{t('onboarding.visual.power')}</span>
                      <span className="text-xl font-mono text-white">200<span className="text-xs text-white/40 ml-1">w</span></span>
                   </div>
                 </div>
              </div>

               {/* Arrow - ALWAYS VISIBLE */}
               <div className="flex justify-center text-white/20">
                 <svg className="w-6 h-6 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
               </div>

              {/* With Setup - ALWAYS VISIBLE */}
              <div className="relative">
                 <div className="absolute -inset-2 bg-gradient-to-r from-[#e10600]/20 to-[#ff8700]/20 blur-xl rounded-full opacity-50"></div>
                 <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-[#ff8700] uppercase">{t('onboarding.visual.withSetup')}</span>
                      <span className="text-xs font-bold text-[#e10600] animate-pulse">{t('onboarding.visual.liveTelemetry')}</span>
                    </div>
                    {/* Zone Bar Visual */}
                    <div className="bg-[#1a1a1a] p-4 rounded-xl border border-white/10 shadow-2xl">
                      <div className="flex items-baseline justify-between mb-3">
                        <span className="text-xs text-white/60 uppercase font-bold">{t('onboarding.visual.zoneThreshold')}</span>
                        <span className="text-2xl font-mono font-bold text-[#e10600]">200<span className="text-xs text-white/40 font-normal ml-1">w</span></span>
                      </div>
                      <div className="flex gap-1 h-3 w-full">
                        {[1,2,3].map(i => <div key={i} className="flex-1 rounded-[1px] bg-slate-700/50"></div>)}
                        <div className="flex-[1.5] rounded-[1px] bg-gradient-to-r from-[#e10600] to-[#ff8700] shadow-[0_0_10px_rgba(225,6,0,0.5)]"></div>
                        {[5,6,7].map(i => <div key={i} className="flex-1 rounded-[1px] bg-slate-700/30"></div>)}
                      </div>
                      <div className="mt-2 flex justify-between text-[10px] text-white/30 font-mono">
                        <span>Z1</span>
                        <span>Z7</span>
                      </div>
                    </div>
                 </div>
              </div>
            </div>

            <div className="md:hidden mt-auto pt-6">
              <button 
                onClick={() => setMobileStep(1)}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/10 text-white font-bold text-sm uppercase tracking-wider hover:bg-white/20 transition-all flex items-center justify-center gap-2"
              >
                {t('common.next')} <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>

            <p className="hidden md:block text-[10px] text-white/20 text-center font-mono relative z-10">
              {t('onboarding.visual.disclaimer')}
            </p>
          </div>

          {/* Right Column: Form */}
          <div className={`${mobileStep === 0 ? 'hidden' : 'flex'} md:flex p-6 md:p-8 flex-col justify-center h-full bg-[#141414]`}>
             {/* Mobile Back Button */}
             <div className="md:hidden mb-4">
               <button 
                onClick={() => setMobileStep(0)}
                className="text-white/40 hover:text-white flex items-center gap-1 text-xs uppercase font-bold tracking-wider"
               >
                 <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                 {t('common.back')}
               </button>
             </div>

             <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded bg-gradient-to-br from-[#e10600] to-[#ff8700] flex items-center justify-center shrink-0">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-white tracking-tight">{t('onboarding.title')}</h2>
                </div>
                <p className="text-white/60 text-sm pl-11">
                  {t('onboarding.subtitle')}
                </p>
             </div>

             <div className="space-y-6">
                <div>
                  <label className="block text-xs font-bold text-white/50 uppercase mb-2">
                    {t('training.ftp')}
                  </label>
                  <div className="relative group">
                    <input
                      type="number"
                      value={ftp}
                      onChange={(e) => setFtp(e.target.value)}
                      placeholder="200"
                      className={`w-full px-4 py-3 bg-black/50 border rounded-lg text-white font-mono focus:outline-none transition-colors ${
                        noPowerMeter
                          ? 'border-white/5 text-white/30 cursor-not-allowed'
                          : 'border-white/10 focus:border-[#e10600] group-hover:border-white/20'
                      }`}
                      disabled={noPowerMeter}
                    />
                    <span className="absolute right-4 top-3 text-white/30 text-sm font-medium">W</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2 pl-1">
                    <input
                      type="checkbox"
                      id="noPowerMeter"
                      checked={noPowerMeter}
                      onChange={(e) => {
                        setNoPowerMeter(e.target.checked);
                        if (e.target.checked) setFtp('');
                      }}
                      className="accent-[#e10600] w-3 h-3 rounded-sm"
                    />
                    <label htmlFor="noPowerMeter" className="text-xs text-white/60 cursor-pointer select-none hover:text-white/80 transition-colors">
                      {t('onboarding.noPowerMeter')}
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-white/50 uppercase mb-2">
                    {t('training.maxHeartRate')}
                  </label>
                  <div className="relative group">
                    <input
                      type="number"
                      value={maxHr}
                      onChange={(e) => setMaxHr(e.target.value)}
                      placeholder="185"
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-lg text-white font-mono focus:outline-none focus:border-[#e10600] group-hover:border-white/20 transition-colors"
                    />
                    <span className="absolute right-4 top-3 text-white/30 text-sm font-medium">bpm</span>
                  </div>
                </div>
             </div>

             <div className="mt-8 pt-6 border-t border-white/5 flex items-center justify-between gap-4">
                <button
                  onClick={handleSkip}
                  className="px-4 py-2 text-sm font-medium text-white/40 hover:text-white transition-colors"
                >
                  {t('onboarding.skip')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={((!noPowerMeter && !ftp) || !maxHr) || isSaving}
                  className="flex-1 px-4 py-3 rounded-lg bg-gradient-to-r from-[#e10600] to-[#ff8700] text-white font-bold text-sm uppercase tracking-wider hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#e10600]/20 transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {isSaving ? t('onboarding.saving') : t('onboarding.complete')}
                </button>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}
